import React, { useContext, useEffect, useRef, useState } from 'react'
import { Flex, Form, Typography, Upload } from 'antd'
import Button from '../../../common/Button'
import { CheckCircleOutlined, DeleteOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons'
import { getRequest, postRequest } from '../../../../services/base'
import { useParams } from 'react-router-dom'
import { NavContext } from '../../../../context/global_context'
import SelectInput from '../../../common/SelectInput'
import useDebouncedValue from '../../../../utils/common_function'
import { useIntl } from 'react-intl'

const SalesCodeComplexityForm = (props) => {
  const { vehicle } = useParams()
  const intl = useIntl()
  const [state, dispatch] = useContext(NavContext)
  const [form] = Form.useForm()
  const [harnessOptions, setHarnessOptions] = useState([])
  const [searchFailed, setSearchFailed] = useState(false)
  const [loader, setLoader] = useState(false)
  const [searchHarness, setSearchHarness] = useState('')
  const debouncedToSearchHarness = useDebouncedValue(searchHarness, 1000)
  const [uploaded, setUploaded] = useState(false)
  const formFiles = Form.useWatch('files', form) || []
  const listRef = useRef(null)

  const scrollRowIntoView = (rowEl) => {
    const listEl = listRef.current
    if (!listEl || !rowEl) return
    const listRect = listEl.getBoundingClientRect()
    const rowRect = rowEl.getBoundingClientRect()
    listEl.scrollTo({
      top: listEl.scrollTop + (rowRect.top - listRect.top) - 16,
      behavior: 'smooth',
    })
  }

  const onFinishFailed = ({ errorFields }) => {
    if (!errorFields?.length) return
    const first = [...errorFields]
      .filter((field) => Array.isArray(field.name) && typeof field.name[1] === 'number')
      .sort((a, b) => a.name[1] - b.name[1])[0]
    if (!first) return
    scrollRowIntoView(
      listRef.current?.querySelector(`[data-row-index="${first.name[1]}"]`)
    )
  }

  const getHarnessData = async (searchValue) => {
    setHarnessOptions([])
    setLoader(true)
    let res = await getRequest(`/api/harnesses/?vehicle=${vehicle}&search=${searchValue}&limit=50`)
    // Search-as-you-type: shown in the dropdown, not as a toast per keystroke.
    if (res?.status === false) {
      setSearchFailed(true)
      setHarnessOptions([])
      setLoader(false)
      return
    }
    setSearchFailed(false)
    setHarnessOptions((res.results || []).map(item => ({
      ...item,
      label: item.harness_id,
      value: item.id,
    })))
    setLoader(false)
  }

  useEffect(() => {
    getHarnessData(searchHarness)
  }, [debouncedToSearchHarness])

  const submitSalesCodeFiles = async (values) => {
    dispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'salesCodeComplexity.uploadingCaption' }),
        showLoader: true,
      },
      notification: {},
    })

    const formData = new FormData()
    formData.append('total_files', values.files.length)

    values.files.forEach((item, index) => {
      if (item?.harness) {
        formData.append(`${index}-name`, item.harness)
      }
      const fileList = item?.file || []
      if (fileList.length > 0) {
        formData.append(`${index}-sales_code_file`, fileList[0].originFileObj || fileList[0])
      }
    })
    formData.append('vehicle', vehicle)

    let res = await postRequest(`/api/plugin/sales_code/upload/${vehicle}/`, formData, false, {
      onUploadProgress: (progressEvent) => {
        const totalLength = progressEvent.lengthComputable
          ? progressEvent.total
          : progressEvent.target.getResponseHeader('content-length') ||
            progressEvent.target.getResponseHeader('x-decompressed-content-length')

        const percentageCompleted = Math.round((progressEvent.loaded * 100) / totalLength)
        if (percentageCompleted === 100) {
          dispatch({ type: 'loader', data: {}, notification: {} })
          setUploaded(true)
        }
      },
    })
  }

  if (uploaded) {
    return (
      <Flex align="center" justify="center" vertical gap={12} style={{ padding: 40 }}>
        <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />
        <Typography.Text strong style={{ fontSize: 20 }}>
          {intl.formatMessage({ id: 'salesCodeComplexity.uploadSuccessTitle' })}
        </Typography.Text>
        <Typography.Text type="secondary">
          {intl.formatMessage({ id: 'salesCodeComplexity.uploadSuccessNote' })}
        </Typography.Text>
      </Flex>
    )
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={submitSalesCodeFiles}
      onFinishFailed={onFinishFailed}
      initialValues={{ files: [{}] }}
    >
      <div>
        <div className="upload-grid"> 
          <div className="grid-header-item">{intl.formatMessage({ id: 'generic.harness' })}</div>
          <div className="grid-header-item">{intl.formatMessage({ id: 'salesCodeComplexity.salesCodeFile' })}</div>
          <div></div>
        </div>

        <div className="bulk-upload-list" ref={listRef}>
          <Form.List name="files">
            {(fields, { add, remove }) => (
              <>
              <div className='list-container'>
                {fields.map(({ key, name }) => {
                  const selectedHarnessIds = formFiles
                    .map(f => f?.harness)
                    .filter(Boolean)
                  const otherSelectedIds = selectedHarnessIds.filter(
                    (_, idx) => idx !== name
                  )
                  const rowOptions = harnessOptions.map(opt => ({
                    ...opt,
                    disabled: otherSelectedIds.includes(opt.value),
                  }))

                  return (
                    <div key={key} data-row-index={name} className='upload-grid'>
                      <div className="grid-item-harness">
                        <SelectInput
                          notFoundContent={
                            searchFailed ? (
                              <Typography.Text type="danger" style={{ color: 'var(--color-form-link-danger)' }}>
                                {intl.formatMessage({ id: 'generic.searchFailed' })}
                              </Typography.Text>
                            ) : undefined
                          }
                          name={[name, 'harness']}
                          label=""
                          hideLabel
                          isRequired
                          options={rowOptions}
                          onInputChange={setSearchHarness}
                          formatOption={(option) => option.harness_id}
                          formatLabel={(option) => option?.harness_id || option?.label}
                          showLoader={loader}
                          allowClear
                          showSearch
                          placeholder={intl.formatMessage({ id: 'salesCodeComplexity.selectHarnessPlaceholder' })}
                          wrapperClass="select-harness"
                        />
                      </div>

                      <div className="grid-item-file">
                        <Form.Item
                          name={[name, 'file']}
                          valuePropName="fileList"
                          getValueFromEvent={(e) => e?.fileList || []}
                          rules={[{ required: true }]}
                          help={false}
                          style={{ marginBottom: 0 }}
                        >
                          <Upload
                            beforeUpload={() => false}
                            maxCount={1}
                            accept=".txt"
                          >
                            <Button icon={<UploadOutlined />}>{intl.formatMessage({ id: 'salesCodeComplexity.chooseFile' })}</Button>
                          </Upload>
                        </Form.Item>
                      </div>

                      <div className="grid-item-delete">
                        <Button
                          type="link"
                          className="link-button danger"
                          icon={<DeleteOutlined />}
                          disabled={fields.length === 1}
                          onClick={() => remove(name)}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className="grid-footer">
                <Flex justify="space-between" style={{ marginTop: 16 }}>
                  <Button
                    type="link"
                    onClick={() => {
                      const nextIndex = fields.length
                      add()
                      requestAnimationFrame(() => {
                        scrollRowIntoView(
                          listRef.current?.querySelector(
                            `[data-row-index="${nextIndex}"]`
                          )
                        )
                      })
                    }}
                    className="link-button"
                    icon={<PlusOutlined />}
                  >
                    {intl.formatMessage({ id: 'salesCodeComplexity.addAnotherFile' })}
                  </Button>
                  <Button type="primary" htmlType="submit">
                    {intl.formatMessage({ id: 'salesCodeComplexity.saveSalesCodeFiles' })}
                  </Button>
                </Flex>
              </div>
              </>
            )}
          </Form.List>
        </div>
      </div>
    </Form>
  )
}

export default SalesCodeComplexityForm
